import type { Auth, CredentialResponseType } from '@@/types/types'

const DEFAULT_AUTH_STATE = {
  authenticated: false,
  user: {},
} as Auth

export const useAuth = () => {
  const { $apiClient, $cookieStore, $lcRepositories, $localePath } =
    useNuxtApp()
  const {
    public: {
      algoliaSearchKey: defaultAlgoliaKey,
      algoliaId: appId,
      algoliaIndexPrefix,
    },
  } = useRuntimeConfig()

  const auth = useState<Auth & { signUp?: boolean }>(
    'auth',
    () => DEFAULT_AUTH_STATE,
  )
  const credentials = useState<CredentialResponseType | null>(
    'credentials',
    () => null,
  )
  const { clearAllCookies, getAuthCookies, getCookie, setCookie } = $cookieStore
  const { fetchUserInformations, clearUser } = useUser()
  const { fetchProperties } = useUserHouses()
  const { fetchWishlists, clearWishlists } = useMultipleWishlist()

  const authenticated = computed(() => auth.value.authenticated ?? false)
  const signUp = computed(() => auth.value.signUp ?? false)
  const authUserId = computed(() => auth.value.user.id ?? null)
  const authUserIdTracking = computed(() =>
    authUserId.value ? `user_${authUserId.value}` : undefined,
  )
  const userIsAdmin = computed(() => auth.value.user.isAdmin ?? false)
  const userIsPartner = computed(
    () => auth.value.user.partnershipAgency ?? false,
  )
  const userIsOwner = computed(() =>
    Boolean(auth.value.user?.ownedHousesIds?.length),
  )
  const mode = computed(() => {
    if (userIsAdmin.value) return 'admin'
    if (userIsPartner.value) return 'b2b2c'

    return 'client'
  })

  const unsignedClientContracts = computed(
    () => auth.value.user.unsignedClientContracts ?? [],
  )
  const unsignedOwnerContracts = computed(
    () => auth.value.user.unsignedOwnerContracts ?? [],
  )
  const algoliaIds = computed(() => {
    let apiKey = defaultAlgoliaKey
    if (userIsAdmin.value) {
      const token = getCookie('search_admin_token')
      apiKey =
        token ?? auth.value.user.algoliaAdminSearchKey ?? defaultAlgoliaKey
    }
    if (userIsPartner.value) {
      const token = getCookie('search_b2b2c_token')
      apiKey =
        token ?? auth.value.user.algoliaB2b2cSearchKey ?? defaultAlgoliaKey
    }
    return {
      apiKey,
      appId,
      prefix: algoliaIndexPrefix as 'staging' | 'production',
    }
  })

  const setHeaders = () => {
    const kebabHeaders = getAuthCookies('kebab')
    const camelHeaders = getAuthCookies('camel')
    if (kebabHeaders && camelHeaders) {
      $apiClient.setHeaders(kebabHeaders)
      $lcRepositories.headers.setHeaders(camelHeaders)
    }
  }

  const setAlgoliaCookies = () => {
    const { user } = auth.value
    if (userIsAdmin.value && user.algoliaAdminSearchKey)
      setCookie('search_admin_token', user.algoliaAdminSearchKey)
    if (userIsPartner.value && user.algoliaB2b2cSearchKey)
      setCookie('search_b2b2c_token', user.algoliaB2b2cSearchKey)
  }

  const clearHeaders = () => {
    $apiClient.clearHeaders()
    $lcRepositories.headers.clearHeaders()
  }

  const resetAuth = (payload?: Auth & { signUp?: boolean }) => {
    auth.value = payload?.authenticated ? payload : DEFAULT_AUTH_STATE
    if (payload?.authenticated) {
      setAlgoliaCookies()
      setHeaders()
    } else {
      clearAllCookies()
      clearHeaders()
    }
  }

  const signContract = (
    contractId: string,
    flowType: 'unsignedClientContracts' | 'unsignedOwnerContracts',
  ) => {
    resetAuth({
      authenticated: authenticated.value,
      user: {
        ...auth.value.user,
        [flowType]: (auth.value.user[flowType] ?? []).filter(
          (id) => id.toString() !== contractId,
        ),
      },
    })
  }

  const login = async (user: Auth['user']) => {
    resetAuth({ authenticated: Boolean(user), user })
    await Promise.all([
      fetchUserInformations(),
      fetchProperties(),
      fetchWishlists(),
    ])
  }

  const setAuthenticated = (value: boolean) => {
    auth.value.authenticated = value
  }

  const setSignUp = (value: boolean) => {
    auth.value.signUp = value
  }

  const setCredentials = (data: CredentialResponseType) => {
    credentials.value = data
  }

  const logout = async (
    payload: { redirect: boolean; returnTo?: string } = { redirect: false },
  ) => {
    if (authenticated.value)
      await $fetch('/api/auth/logout').catch(console.warn)
    resetAuth()
    credentials.value = null
    clearUser()
    clearWishlists()

    if (payload.redirect) {
      const path = $localePath({
        name: 'login',
        ...(payload.returnTo
          ? { query: { return_to: payload.returnTo } }
          : null),
      })
      return navigateTo(path)
    }
  }

  return {
    algoliaIds,
    authenticated,
    authUserIdTracking,
    clearHeaders,
    credentials,
    login,
    logout,
    mode,
    resetAuth,
    setAuthenticated,
    setCredentials,
    setHeaders,
    setSignUp,
    signContract,
    signUp,
    unsignedClientContracts,
    unsignedOwnerContracts,
    userIsAdmin,
    userIsOwner,
    userIsPartner,
  }
}
